import { FollowUpOutcomeSplit, SymptomCategory, SymptomChange } from '@estercare/ester-shared'
import { theme } from '../../theme/theme'
import { ChartStep, SeverityByConditionRow } from '../../components/charts/type'

export const OUTCOME_KEYS = {
  improved: 'improved',
  unchanged: 'unchanged',
  worsened: 'worsened',
} as const

export type OutcomeKey = (typeof OUTCOME_KEYS)[keyof typeof OUTCOME_KEYS]

export const getOutcomeSteps = (t: (key: string) => string): ChartStep[] => [
  {
    key: OUTCOME_KEYS.improved,
    name: t('admin.statistics.outcomeImproved'),
    color: theme.color.green,
    ink: theme.color.black,
  },
  {
    key: OUTCOME_KEYS.unchanged,
    name: t('admin.statistics.outcomeUnchanged'),
    color: theme.color.beigeDark,
    ink: theme.color.black,
  },
  {
    key: OUTCOME_KEYS.worsened,
    name: t('admin.statistics.outcomeWorsened'),
    color: theme.color.red,
    ink: theme.color.white,
  },
]

const SYMPTOM_LABEL_KEYS: Partial<Record<SymptomCategory, string>> = {
  [SymptomCategory.MenstrualPain]: 'admin.statistics.symptomMenstrualPain',
  [SymptomCategory.HotFlashes]: 'admin.statistics.symptomHotFlashes',
  [SymptomCategory.BrainFog]: 'admin.statistics.symptomBrainFog',
  [SymptomCategory.SleepDifficulties]: 'admin.statistics.symptomSleepDifficulties',
  [SymptomCategory.Irritability]: 'admin.statistics.symptomIrritability',
}

export const getOutcomeValues = ({
  improvedPct,
  unchangedPct,
  worsenedPct,
}: FollowUpOutcomeSplit): Record<OutcomeKey, number> => ({
  [OUTCOME_KEYS.improved]: improvedPct,
  [OUTCOME_KEYS.unchanged]: unchangedPct,
  [OUTCOME_KEYS.worsened]: worsenedPct,
})

export const getSymptomChangeRows = (
  t: (key: string) => string,
  symptomChanges: readonly SymptomChange[]
): SeverityByConditionRow[] =>
  symptomChanges.flatMap((change) => {
    const labelKey = SYMPTOM_LABEL_KEYS[change.category]
    if (!labelKey) {
      // eslint-disable-next-line no-console
      console.warn(`Health program statistics returned an unrecognised symptom category: ${change.category}`)
      return []
    }
    return [
      {
        key: change.category,
        label: t(labelKey),
        values: getOutcomeValues(change),
      },
    ]
  })
